import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AdminOrderService } from '../../../services/admin-order.service';
import { ToastService } from '../../../services/toast.service';
import { AdminOrder } from '../order-management/order-management.component';
import { AppointmentService, Appointment } from '../../../services/appointment.service';
import * as ExcelJS from 'exceljs';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, OnDestroy {
  // Expose Math to template
  Math = Math;
  
  orders: AdminOrder[] = [];
  filteredOrders: AdminOrder[] = [];
  appointments: Appointment[] = [];
  filteredAppointments: Appointment[] = [];
  isLoading = false;
  isLoadingAppointments = false;
  activeTab: 'orders' | 'appointments' = 'orders';
  startDate: string = '';
  endDate: string = '';
  appointmentStartDate: string = '';
  appointmentEndDate: string = '';
  totalRevenue: number = 0; // Revenue from paid/delivered orders
  filteredRevenue: number = 0; // Revenue from filtered paid/delivered orders
  totalOrderValue: number = 0; // Total value of all orders
  filteredOrderValue: number = 0; // Total value of filtered orders

  // Pagination for Orders
  currentOrdersPage: number = 1;
  ordersPerPage: number = 10;
  totalOrdersPages: number = 1;

  // Pagination for Appointments
  currentAppointmentsPage: number = 1;
  appointmentsPerPage: number = 10;
  totalAppointmentsPages: number = 1;

  // Maximum date (today) for date inputs
  maxDate: string = '';

  private destroy$ = new Subject<void>();
  private readonly ORDERS_COLLECTION = 'orderTable';

  constructor(
    private adminOrderService: AdminOrderService,
    private toastService: ToastService,
    private appointmentService: AppointmentService
  ) {
    // Set maximum date to today (no future dates allowed)
    const today = new Date();
    this.maxDate = this.formatDateForInput(today);
    
    // Set default date range to last 30 days
    const endDateObj = new Date();
    const startDateObj = new Date();
    startDateObj.setDate(startDateObj.getDate() - 30);
    
    this.endDate = this.formatDateForInput(endDateObj);
    this.startDate = this.formatDateForInput(startDateObj);
  }

  ngOnInit() {
    this.loadAllOrders();
    this.loadAllAppointments();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadAllOrders() {
    try {
      this.isLoading = true;
      const allOrders: AdminOrder[] = [];
      let lastDoc: any = null;
      let hasMore = true;

      // Load all orders using pagination
      while (hasMore) {
        const result = await this.adminOrderService.getAdminOrders(100, lastDoc)
          .pipe(takeUntil(this.destroy$))
          .toPromise();
        
        if (result && result.orders) {
          allOrders.push(...result.orders);
          lastDoc = result.lastDoc;
          hasMore = result.hasMore && result.orders.length > 0;
        } else {
          hasMore = false;
        }
      }

      this.orders = allOrders;
      this.totalRevenue = this.calculateRevenue(this.orders);
      this.totalOrderValue = this.calculateTotalOrderValue(this.orders);
      this.currentOrdersPage = 1;
      this.applyFilters();
      
      if (this.orders.length === 0) {
        this.toastService.info('No orders found in the system');
      }
    } catch (error: any) {
      console.error('Error loading orders:', error);
      this.toastService.error('Failed to load orders. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  onDateFilterChange() {
    // Validate dates
    if (this.startDate && this.isFutureDate(this.startDate)) {
      this.toastService.error('Start date cannot be in the future');
      this.startDate = '';
      return;
    }
    if (this.endDate && this.isFutureDate(this.endDate)) {
      this.toastService.error('End date cannot be in the future');
      this.endDate = '';
      return;
    }
    this.applyFilters();
  }

  isFutureDate(dateString: string): boolean {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date > today;
  }

  applyFilters() {
    if (!this.startDate && !this.endDate) {
      this.filteredOrders = [...this.orders];
      this.filteredRevenue = this.totalRevenue;
      this.filteredOrderValue = this.totalOrderValue;
      this.updateOrdersPagination();
      return;
    }

    const startDateObj = this.startDate ? new Date(this.startDate) : null;
    const endDateObj = this.endDate ? new Date(this.endDate) : null;

    // Set end date to end of day for inclusive filtering
    if (endDateObj) {
      endDateObj.setHours(23, 59, 59, 999);
    }

    this.filteredOrders = this.orders.filter(order => {
      const orderDate = this.getOrderDate(order);
      if (!orderDate) return false;

      const orderDateObj = orderDate instanceof Date ? orderDate : new Date(orderDate);

      if (startDateObj && orderDateObj < startDateObj) {
        return false;
      }
      if (endDateObj && orderDateObj > endDateObj) {
        return false;
      }

      return true;
    });

    this.filteredRevenue = this.calculateRevenue(this.filteredOrders);
    this.filteredOrderValue = this.calculateTotalOrderValue(this.filteredOrders);
    this.updateOrdersPagination();
  }

  getOrderDate(order: AdminOrder): Date | null {
    if (!order.orderDate) return null;
    
    try {
      if (order.orderDate.seconds) {
        return new Date(order.orderDate.seconds * 1000);
      } else if (order.orderDate.toDate && typeof order.orderDate.toDate === 'function') {
        return order.orderDate.toDate();
      } else if (order.orderDate instanceof Date) {
        return order.orderDate;
      } else if (typeof order.orderDate === 'string') {
        return new Date(order.orderDate);
      } else if (typeof order.orderDate === 'number') {
        return new Date(order.orderDate);
      }
      return new Date(order.orderDate);
    } catch (error) {
      console.error('Error parsing order date:', error, order.orderDate);
      return null;
    }
  }

  calculateRevenue(orders: AdminOrder[]): number {
    return orders.reduce((total, order) => {
      // Only count revenue from delivered orders with completed payment
      if (order.status === 'delivered' && order.paymentStatus === 'completed') {
        const orderTotal = order.totalAmount - (order.discountAmount || 0) + (order.deliveryCharges || 0);
        return total + orderTotal;
      }
      return total;
    }, 0);
  }

  calculateTotalOrderValue(orders: AdminOrder[]): number {
    return orders.reduce((total, order) => {
      // Count total value of all orders (excluding cancelled)
      if (order.status !== 'cancelled') {
        const orderTotal = order.totalAmount - (order.discountAmount || 0) + (order.deliveryCharges || 0);
        return total + orderTotal;
      }
      return total;
    }, 0);
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    try {
      let dateObj: Date;
      
      if (date.seconds) {
        dateObj = new Date(date.seconds * 1000);
      } else if (date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
      } else {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) {
        return 'N/A';
      }
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  getStatusColor(status: string): string {
    const statusColors: { [key: string]: string } = {
      pending: '#ffc107',
      confirmed: '#17a2b8',
      shipped: '#007bff',
      delivered: '#28a745',
      cancelled: '#dc3545'
    };
    return statusColors[status] || '#6c757d';
  }

  getPaymentStatusText(status: string): string {
    const statusTexts: { [key: string]: string } = {
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed'
    };
    return statusTexts[status] || status;
  }

  getOrderTotal(order: AdminOrder): number {
    return order.totalAmount - (order.discountAmount || 0) + (order.deliveryCharges || 0);
  }

  async exportToExcel() {
    try {
      if (this.filteredOrders.length === 0) {
        this.toastService.warning('No orders to export');
        return;
      }

      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Orders Report');

      // Define columns with widths
      // worksheet.columns = [
      //   { header: 'Order ID', key: 'orderId', width: 15 },
      //   { header: 'Order Date', key: 'orderDate', width: 20 },
      //   { header: 'Customer Name', key: 'customerName', width: 20 },
      //   { header: 'Mobile', key: 'mobile', width: 15 },
      //   { header: 'Email', key: 'email', width: 25 },
      //   { header: 'Products', key: 'products', width: 40 },
      //   { header: 'Total Amount', key: 'totalAmount', width: 15 },
      //   { header: 'Discount', key: 'discount', width: 12 },
      //   { header: 'Delivery Charges', key: 'deliveryCharges', width: 15 },
      //   { header: 'Final Amount', key: 'finalAmount', width: 15 },
      //   { header: 'Status', key: 'status', width: 12 },
      //   { header: 'Payment Method', key: 'paymentMethod', width: 15 },
      //   { header: 'Payment Status', key: 'paymentStatus', width: 15 },
      //   { header: 'Delivery Address', key: 'deliveryAddress', width: 40 },
      //   { header: 'Tracking Number', key: 'trackingNumber', width: 18 },
      //   { header: 'Courier', key: 'courier', width: 20 }
      // ];

      // Helper function to get status color
      const getStatusColor = (status: string): string => {
        const colorMap: { [key: string]: string } = {
          'pending': 'FFFFC107',    // Yellow
          'confirmed': 'FF17A2B8',  // Teal
          'shipped': 'FF007BFF',    // Blue
          'delivered': 'FF28A745',  // Green
          'cancelled': 'FFDC3545'   // Red
        };
        return colorMap[status?.toLowerCase()] || 'FF6C757D'; // Gray default
      };

      // Helper function to apply cell styling
      const applyCellStyle = (cell: ExcelJS.Cell, options: {
        fill?: string;
        font?: Partial<ExcelJS.Font>;
        alignment?: Partial<ExcelJS.Alignment>;
        border?: Partial<ExcelJS.Borders>;
        numFmt?: string;
      }) => {
        if (options.fill) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: options.fill }
          };
        }
        if (options.font) {
          cell.font = options.font;
        }
        if (options.alignment) {
          cell.alignment = options.alignment;
        }
        if (options.border) {
          cell.border = options.border;
        }
        if (options.numFmt) {
          cell.numFmt = options.numFmt;
        }
      };

      // Summary rows with clear labels - Start from row 1
      const summaryRows = [
        { label: 'Total Orders', value: this.filteredOrders.length, fill: 'FFE9ECEF', isCurrency: false },
        { label: 'Revenue Received', value: this.filteredOrderValue, isCurrency: true, fill: 'FF28A745' },
      ];

      summaryRows.forEach(({ label, value, isCurrency, fill }) => {
        const summaryRow = worksheet.addRow([label, value, '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
        
        const labelCell = summaryRow.getCell(1);
        const valueCell = summaryRow.getCell(2);
        
        // Style the label cell
        applyCellStyle(labelCell, {
          fill: fill,
          font: { bold: true, size: 11 },
          alignment: { horizontal: 'left', vertical: 'middle' },
          border: {
            top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
          }
        });

        // Style the value cell
        applyCellStyle(valueCell, {
          fill: fill === 'FF28A745' ? 'FF28A745' : fill,
          font: { bold: true, size: 11, color: fill === 'FF28A745' ? { argb: 'FFFFFFFF' } : undefined },
          alignment: { horizontal: 'right', vertical: 'middle' },
          numFmt: isCurrency ? '#,##0.00' : '#,##0',
          border: {
            top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
          }
        });

        // Set the values - label in first column, value in second
        labelCell.value = label;
        valueCell.value = value;
        
        summaryRow.height = 20;
      });

      // Set column widths for summary section (columns 1 and 2)
      worksheet.getColumn(1).width = 25; // Label column
      worksheet.getColumn(2).width = 20; // Value column

      // Add empty row after summary
      const emptyRow = worksheet.addRow(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
      emptyRow.height = 10;

      // Add table headers
      const headers = [
        'Order ID', 'Order Date', 'Customer Name', 'Mobile', 'Email',
        'Products', 'Sub Category', 'Total Amount', 'Discount', 'Delivery Charges', 'Final Amount',
        'Status', 'Payment Method', 'Payment Status', 'Delivery Address',
        'Tracking Number', 'Courier'
      ];
      
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 25;
      
      // Set column widths for the main table (starting from column 1)
      worksheet.getColumn(1).width = 15;  // Order ID
      worksheet.getColumn(2).width = 20;  // Order Date
      worksheet.getColumn(3).width = 20;  // Customer Name
      worksheet.getColumn(4).width = 15;  // Mobile
      worksheet.getColumn(5).width = 25;  // Email
      worksheet.getColumn(6).width = 30;  // Products
      worksheet.getColumn(7).width = 25;  // Sub Category
      worksheet.getColumn(8).width = 15;  // Total Amount
      worksheet.getColumn(9).width = 12;  // Discount
      worksheet.getColumn(10).width = 15; // Delivery Charges
      worksheet.getColumn(11).width = 15; // Final Amount
      worksheet.getColumn(12).width = 12; // Status
      worksheet.getColumn(13).width = 15; // Payment Method
      worksheet.getColumn(14).width = 15; // Payment Status
      worksheet.getColumn(15).width = 40; // Delivery Address
      worksheet.getColumn(16).width = 18; // Tracking Number
      worksheet.getColumn(17).width = 20; // Courier
      
      // Style header row
      headerRow.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell, colNumber: number) => {
        applyCellStyle(cell, {
          fill: 'FF3498DB',
          font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
          alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
          border: {
            top: { style: 'thin', color: { argb: 'FF2980B9' } },
            bottom: { style: 'thin', color: { argb: 'FF2980B9' } },
            left: { style: 'thin', color: { argb: 'FF2980B9' } },
            right: { style: 'thin', color: { argb: 'FF2980B9' } }
          }
        });
        // Ensure word wrap is enabled in header for all columns
        if (!cell.alignment) {
          cell.alignment = {};
        }
        cell.alignment.wrapText = true;
        // For text-heavy columns (Products, Sub Category, Delivery Address), allow left alignment
        if (colNumber === 6 || colNumber === 7 || colNumber === 15) {
          cell.alignment.horizontal = 'left';
        }
      });

      // Add order data rows
      this.filteredOrders.forEach((order, index) => {
        const orderDate = this.getOrderDate(order);
        const productsList = (order.products || [])
          .map((p: any) => p.name || 'N/A')
          .join('; ');
        
        const subCategoriesList = (order.products || [])
          .map((p: any) => p.selectedCategoryName || p.categoryName || 'N/A')
          .join('; ');

        const row = worksheet.addRow([
          order.id,
          orderDate ? orderDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'N/A',
          order.userName || 'N/A',
          order.userMobile || 'N/A',
          order.userEmail || 'N/A',
          productsList,
          subCategoriesList,
          order.totalAmount || 0,
          order.discountAmount || 0,
          order.deliveryCharges || 0,
          this.getOrderTotal(order),
          order.status || 'N/A',
          order.paymentMethod || 'N/A',
          this.getPaymentStatusText(order.paymentStatus || 'pending'),
          order.deliveryAddress || 'N/A',
          order.trackingNumber || 'N/A',
          order.shiprocketCourierName || order.shippingProvider || 'N/A'
        ]);

        // Alternate row colors
        const rowFill = index % 2 === 0 ? 'FFFFFFFF' : 'FFF8F9FA';
        const statusColor = getStatusColor(order.status || '');

        row.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell, colNumber: number) => {
          // Apply alternating row colors
          if (colNumber !== 12) { // Don't override status column color (now column 12)
            applyCellStyle(cell, {
              fill: rowFill,
              border: {
                top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
                bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
                left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
                right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
              }
            });
          }

          // Format currency columns (8, 9, 10, 11) - shifted due to new Sub Category column
          if (colNumber >= 8 && colNumber <= 11) {
            applyCellStyle(cell, {
              numFmt: '#,##0.00',
              alignment: { horizontal: 'right', vertical: 'middle' }
            });
          }

          // Status column with color coding (now column 12)
          if (colNumber === 12) {
            applyCellStyle(cell, {
              fill: statusColor,
              font: { bold: true, color: { argb: 'FFFFFFFF' } },
              alignment: { horizontal: 'center', vertical: 'middle' },
              border: {
                top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
                bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
                left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
                right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
              }
            });
          }

          // Payment status column (now column 14)
          if (colNumber === 14) {
            const paymentStatusColor = order.paymentStatus === 'completed' ? 'FF28A745' : 
                                     order.paymentStatus === 'failed' ? 'FFDC3545' : 'FFFFC107';
            applyCellStyle(cell, {
              fill: paymentStatusColor,
              font: { bold: true, color: { argb: 'FFFFFFFF' } },
              alignment: { horizontal: 'center', vertical: 'middle' }
            });
          }

          // Center align certain columns
          if (colNumber === 1 || colNumber === 4 || colNumber === 13) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          }

          // Wrap text for products, subcategory, email, and address columns
          if (colNumber === 5 || colNumber === 6 || colNumber === 7 || colNumber === 15) {
            if (!cell.alignment) {
              cell.alignment = {};
            }
            cell.alignment.horizontal = 'left';
            cell.alignment.vertical = 'top';
            cell.alignment.wrapText = true;
          }
        });

        // Set row height to accommodate word wrap - calculate based on content length
        const maxContentLength = Math.max(
          productsList.length,
          subCategoriesList.length,
          (order.deliveryAddress || '').length
        );
        if (maxContentLength > 100) {
          row.height = 40; // Larger height for rows with long text
        } else if (maxContentLength > 50) {
          row.height = 30;
        } else {
          row.height = 20; // Normal height
        }
      });

      // Add total row at the bottom
      const totalRow = worksheet.addRow([
        'TOTAL',
        '',
        '',
        '',
        '',
        '', // Products column
        '', // Sub Category column - empty for total row
        this.filteredOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0),
        this.filteredOrders.reduce((sum, o) => sum + (o.discountAmount || 0), 0),
        this.filteredOrders.reduce((sum, o) => sum + (o.deliveryCharges || 0), 0),
        this.filteredOrderValue,
        '',
        '',
        '',
        '',
        '',
        ''
      ]);

      totalRow.height = 25;
      totalRow.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell, colNumber: number) => {
        applyCellStyle(cell, {
          fill: 'FF2C3E50',
          font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
          alignment: { horizontal: colNumber === 1 ? 'left' : 'right', vertical: 'middle' },
          numFmt: colNumber >= 8 && colNumber <= 11 ? '#,##0.00' : undefined, // Currency columns shifted
          border: {
            top: { style: 'medium', color: { argb: 'FF1A252F' } },
            bottom: { style: 'medium', color: { argb: 'FF1A252F' } },
            left: { style: 'thin', color: { argb: 'FF1A252F' } },
            right: { style: 'thin', color: { argb: 'FF1A252F' } }
          }
        });
      });

      // Generate filename with date range
      const startDateStr = this.startDate || 'All';
      const endDateStr = this.endDate || 'All';
      const filename = `Orders_Report_${startDateStr}_to_${endDateStr}.xlsx`;

      // Write file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      this.toastService.success(`Exported ${this.filteredOrders.length} orders to Excel with formatting`);
    } catch (error: any) {
      console.error('Error exporting to Excel:', error);
      this.toastService.error('Failed to export to Excel. Please try again.');
    }
  }

  clearFilters() {
    this.startDate = '';
    this.endDate = '';
    this.currentOrdersPage = 1;
    this.applyFilters();
  }

  // Pagination methods for Orders
  updateOrdersPagination() {
    this.totalOrdersPages = Math.ceil(this.filteredOrders.length / this.ordersPerPage);
    if (this.currentOrdersPage > this.totalOrdersPages && this.totalOrdersPages > 0) {
      this.currentOrdersPage = this.totalOrdersPages;
    }
  }

  getPaginatedOrders(): AdminOrder[] {
    const startIndex = (this.currentOrdersPage - 1) * this.ordersPerPage;
    const endIndex = startIndex + this.ordersPerPage;
    return this.filteredOrders.slice(startIndex, endIndex);
  }

  goToOrdersPage(page: number) {
    if (page >= 1 && page <= this.totalOrdersPages) {
      this.currentOrdersPage = page;
    }
  }

  previousOrdersPage() {
    if (this.currentOrdersPage > 1) {
      this.currentOrdersPage--;
    }
  }

  nextOrdersPage() {
    if (this.currentOrdersPage < this.totalOrdersPages) {
      this.currentOrdersPage++;
    }
  }

  // Tab switching
  switchTab(tab: 'orders' | 'appointments') {
    this.activeTab = tab;
  }

  // Appointments methods
  async loadAllAppointments() {
    try {
      this.isLoadingAppointments = true;
      this.appointmentService.getAllAppointments()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (appointments) => {
            this.appointments = appointments;
            // Set default date range to last 30 days
            const endDateObj = new Date();
            const startDateObj = new Date();
            startDateObj.setDate(startDateObj.getDate() - 30);
            
            this.appointmentEndDate = this.formatDateForInput(endDateObj);
            this.appointmentStartDate = this.formatDateForInput(startDateObj);
            this.currentAppointmentsPage = 1;
            this.applyAppointmentFilters();
            
            if (this.appointments.length === 0) {
              this.toastService.info('No appointments found in the system');
            }
          },
          error: (error) => {
            console.error('Error loading appointments:', error);
            this.toastService.error('Failed to load appointments. Please try again.');
          },
          complete: () => {
            this.isLoadingAppointments = false;
          }
        });
    } catch (error: any) {
      console.error('Error loading appointments:', error);
      this.toastService.error('Failed to load appointments. Please try again.');
      this.isLoadingAppointments = false;
    }
  }

  onAppointmentDateFilterChange() {
    // Validate dates
    if (this.appointmentStartDate && this.isFutureDate(this.appointmentStartDate)) {
      this.toastService.error('Start date cannot be in the future');
      this.appointmentStartDate = '';
      return;
    }
    if (this.appointmentEndDate && this.isFutureDate(this.appointmentEndDate)) {
      this.toastService.error('End date cannot be in the future');
      this.appointmentEndDate = '';
      return;
    }
    this.applyAppointmentFilters();
  }

  applyAppointmentFilters() {
    if (!this.appointmentStartDate && !this.appointmentEndDate) {
      this.filteredAppointments = [...this.appointments];
      this.updateAppointmentsPagination();
      return;
    }

    const startDateObj = this.appointmentStartDate ? new Date(this.appointmentStartDate) : null;
    const endDateObj = this.appointmentEndDate ? new Date(this.appointmentEndDate) : null;

    // Set end date to end of day for inclusive filtering
    if (endDateObj) {
      endDateObj.setHours(23, 59, 59, 999);
    }

    this.filteredAppointments = this.appointments.filter(appointment => {
      const appointmentDate = this.getAppointmentDate(appointment);
      if (!appointmentDate) return false;

      const appointmentDateObj = appointmentDate instanceof Date ? appointmentDate : new Date(appointmentDate);

      if (startDateObj && appointmentDateObj < startDateObj) {
        return false;
      }
      if (endDateObj && appointmentDateObj > endDateObj) {
        return false;
      }

      return true;
    });
    this.updateAppointmentsPagination();
  }

  getAppointmentDate(appointment: Appointment): Date | null {
    if (!appointment.createdAt) return null;
    
    try {
      if (appointment.createdAt.seconds) {
        return new Date(appointment.createdAt.seconds * 1000);
      } else if (appointment.createdAt.toDate && typeof appointment.createdAt.toDate === 'function') {
        return appointment.createdAt.toDate();
      } else if (appointment.createdAt instanceof Date) {
        return appointment.createdAt;
      } else if (typeof appointment.createdAt === 'string') {
        return new Date(appointment.createdAt);
      } else if (typeof appointment.createdAt === 'number') {
        return new Date(appointment.createdAt);
      }
      return new Date(appointment.createdAt);
    } catch (error) {
      console.error('Error parsing appointment date:', error, appointment.createdAt);
      return null;
    }
  }

  getAppointmentStatusColor(status: string): string {
    const statusColors: { [key: string]: string } = {
      pending: '#ffc107',
      confirmed: '#28a745',
      cancelled: '#dc3545'
    };
    return statusColors[status] || '#6c757d';
  }

  clearAppointmentFilters() {
    this.appointmentStartDate = '';
    this.appointmentEndDate = '';
    this.currentAppointmentsPage = 1;
    this.applyAppointmentFilters();
  }

  // Pagination methods for Appointments
  updateAppointmentsPagination() {
    this.totalAppointmentsPages = Math.ceil(this.filteredAppointments.length / this.appointmentsPerPage);
    if (this.currentAppointmentsPage > this.totalAppointmentsPages && this.totalAppointmentsPages > 0) {
      this.currentAppointmentsPage = this.totalAppointmentsPages;
    }
  }

  getPaginatedAppointments(): Appointment[] {
    const startIndex = (this.currentAppointmentsPage - 1) * this.appointmentsPerPage;
    const endIndex = startIndex + this.appointmentsPerPage;
    return this.filteredAppointments.slice(startIndex, endIndex);
  }

  goToAppointmentsPage(page: number) {
    if (page >= 1 && page <= this.totalAppointmentsPages) {
      this.currentAppointmentsPage = page;
    }
  }

  previousAppointmentsPage() {
    if (this.currentAppointmentsPage > 1) {
      this.currentAppointmentsPage--;
    }
  }

  nextAppointmentsPage() {
    if (this.currentAppointmentsPage < this.totalAppointmentsPages) {
      this.currentAppointmentsPage++;
    }
  }

  async exportAppointmentsToExcel() {
    try {
      if (this.filteredAppointments.length === 0) {
        this.toastService.warning('No appointments to export');
        return;
      }

      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Appointments Report');

      // Helper function to apply cell styling
      const applyCellStyle = (cell: ExcelJS.Cell, options: {
        fill?: string;
        font?: Partial<ExcelJS.Font>;
        alignment?: Partial<ExcelJS.Alignment>;
        border?: Partial<ExcelJS.Borders>;
        numFmt?: string;
      }) => {
        if (options.fill) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: options.fill }
          };
        }
        if (options.font) {
          cell.font = options.font;
        }
        if (options.alignment) {
          cell.alignment = options.alignment;
        }
        if (options.border) {
          cell.border = options.border;
        }
        if (options.numFmt) {
          cell.numFmt = options.numFmt;
        }
      };

      // Summary rows
      const summaryRows = [
        { label: 'Total Appointments', value: this.filteredAppointments.length, fill: 'FFE9ECEF', isCurrency: false },
      ];

      summaryRows.forEach(({ label, value, isCurrency, fill }) => {
        const summaryRow = worksheet.addRow([label, value, '', '', '', '', '', '']);
        
        const labelCell = summaryRow.getCell(1);
        const valueCell = summaryRow.getCell(2);
        
        applyCellStyle(labelCell, {
          fill: fill,
          font: { bold: true, size: 11 },
          alignment: { horizontal: 'left', vertical: 'middle' },
          border: {
            top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
          }
        });

        applyCellStyle(valueCell, {
          fill: fill,
          font: { bold: true, size: 11 },
          alignment: { horizontal: 'right', vertical: 'middle' },
          numFmt: isCurrency ? '#,##0.00' : '#,##0',
          border: {
            top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
            right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
          }
        });

        labelCell.value = label;
        valueCell.value = value;
        summaryRow.height = 20;
      });

      worksheet.getColumn(1).width = 25;
      worksheet.getColumn(2).width = 20;

      const emptyRow = worksheet.addRow(['', '', '', '', '', '', '', '']);
      emptyRow.height = 10;

      // Add table headers
      const headers = [
        'Appointment ID', 'Date', 'Name', 'Age', 'Gender',
        'WhatsApp Number', 'Address'
      ];
      
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 25;
      
      // Set column widths
      worksheet.getColumn(1).width = 20;  // Appointment ID
      worksheet.getColumn(2).width = 20;  // Date
      worksheet.getColumn(3).width = 25;  // Name
      worksheet.getColumn(4).width = 10;  // Age
      worksheet.getColumn(5).width = 12;  // Gender
      worksheet.getColumn(6).width = 18;  // WhatsApp Number
      worksheet.getColumn(7).width = 40; // Address
      
      // Style header row
      headerRow.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell) => {
        applyCellStyle(cell, {
          fill: 'FF3498DB',
          font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } },
          alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
          border: {
            top: { style: 'thin', color: { argb: 'FF2980B9' } },
            bottom: { style: 'thin', color: { argb: 'FF2980B9' } },
            left: { style: 'thin', color: { argb: 'FF2980B9' } },
            right: { style: 'thin', color: { argb: 'FF2980B9' } }
          }
        });
      });

      // Add appointment data rows
      this.filteredAppointments.forEach((appointment, index) => {
        const appointmentDate = this.getAppointmentDate(appointment);

        const row = worksheet.addRow([
          appointment.id,
          appointmentDate ? appointmentDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'N/A',
          appointment.name || 'N/A',
          appointment.age || 'N/A',
          appointment.gender || 'N/A',
          appointment.whatsappNumber || 'N/A',
          appointment.address || 'N/A'
        ]);

        const rowFill = index % 2 === 0 ? 'FFFFFFFF' : 'FFF8F9FA';

        row.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell, colNumber: number) => {
          applyCellStyle(cell, {
            fill: rowFill,
            border: {
              top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
              bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
              left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
              right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
            }
          });

          // Center align certain columns
          if (colNumber === 1 || colNumber === 4 || colNumber === 5) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          }

          // Wrap text for address column
          if (colNumber === 7) {
            if (!cell.alignment) {
              cell.alignment = {};
            }
            cell.alignment.horizontal = 'left';
            cell.alignment.vertical = 'top';
            cell.alignment.wrapText = true;
          }
        });

        const addressLength = (appointment.address || '').length;
        if (addressLength > 100) {
          row.height = 40;
        } else if (addressLength > 50) {
          row.height = 30;
        } else {
          row.height = 20;
        }
      });

      // Generate filename with date range
      const startDateStr = this.appointmentStartDate || 'All';
      const endDateStr = this.appointmentEndDate || 'All';
      const filename = `Appointments_Report_${startDateStr}_to_${endDateStr}.xlsx`;

      // Write file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      this.toastService.success(`Exported ${this.filteredAppointments.length} appointments to Excel`);
    } catch (error: any) {
      console.error('Error exporting appointments to Excel:', error);
      this.toastService.error('Failed to export to Excel. Please try again.');
    }
  }
}
